import { niceStep } from "@pervasivemind/bi-components";

import { formatValue } from "./formatting";

/**
 * Pure helpers for the numeric ``range`` filter (``{"min": n, "max": n}``).
 *
 * Wire contract, mirrors ``report_engine.parse_range_value``:
 *   - the value is in the COLUMN's units (a 0..1 fraction column filtered
 *     at 30 % travels as ``{"min": 0.3}``); ``value_format`` only changes
 *     what the widget and the chip SHOW;
 *   - ``null`` / absent = that side is open; both open = ``""`` (cleared);
 *   - ``min <= max`` always: the engine answers 422 to an inverted pair,
 *     so the widget sorts the pair before emitting.
 */

export type RangeValueFormat = "number" | "percent" | "currency";

export interface RangeBounds {
  min: number | null;
  max: number | null;
}

function finite(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Decode a wire value. ``null`` for empty or malformed input, the
 *  caller shows the open range; the engine is what rejects bad values. */
export function decodeRange(raw: string | null | undefined): RangeBounds | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const obj = parsed as Record<string, unknown>;
  return { min: finite(obj.min), max: finite(obj.max) };
}

/** Encode a pair, sorted; both sides open → ``""`` (cleared). */
export function encodeRange(min: number | null, max: number | null): string {
  let lo = min;
  let hi = max;
  if (lo !== null && hi !== null && lo > hi) [lo, hi] = [hi, lo];
  if (lo === null && hi === null) return "";
  const out: { min?: number; max?: number } = {};
  if (lo !== null) out.min = lo;
  if (hi !== null) out.max = hi;
  return JSON.stringify(out);
}

/** Round away binary noise (0.1 * 3 → 0.3) without losing real digits. */
export function clean(n: number): number {
  return Number(n.toPrecision(12));
}

/**
 * Snap points of the slider track over the engine's GLOBAL ``[lo, hi]``
 * (served as numeric strings). Both real extremes are included, inner
 * points sit on multiples of ``niceStep((hi - lo) / 100)``, so 0.30 or
 * 2,000,000 are exactly reachable. Missing / unparseable / inverted
 * bounds → ``[]``: the widget then shows only the exact-value inputs.
 * Never an invented domain.
 */
export function buildNumericDomain(
  bounds: ReadonlyArray<string> | undefined,
  scale: string | null | undefined = "linear",
): number[] {
  if (!bounds || bounds.length < 2) return [];
  const lo = Number(bounds[0]);
  const hi = Number(bounds[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) return [];
  if (lo === hi) return [lo];
  if (scale === "log") return buildLogDomain(lo, hi);
  const step = niceStep((hi - lo) / 100);
  const out = [lo];
  for (let k = Math.floor(lo / step) + 1; k * step < hi; k++) {
    const p = clean(k * step);
    if (p > lo && p < hi) out.push(p);
  }
  out.push(hi);
  return out;
}

/** Two significant figures: $2,345,678 → $2,300,000, 0.0347 → 0.035. */
function twoSig(v: number): number {
  if (v === 0) return 0;
  const p = Math.pow(10, Math.floor(Math.log10(Math.abs(v))) - 1);
  return clean(Math.round(v / p) * p);
}

/**
 * Log-scale snap points: 100 steps evenly spaced in log10 between the
 * real extremes, rounded to two significant figures ($2.0M, $150K), both
 * extremes kept exact. Needs ``lo > 0``: a log track over zero or a
 * negative value has no answer, so it returns ``[]`` and the widget says
 * why (``logScaleUnusable``) instead of quietly drawing a linear track.
 */
function buildLogDomain(lo: number, hi: number): number[] {
  if (lo <= 0) return [];
  const a = Math.log10(lo);
  const b = Math.log10(hi);
  const out = [lo];
  for (let i = 1; i < 100; i++) {
    const p = twoSig(Math.pow(10, a + ((b - a) * i) / 100));
    if (p > out[out.length - 1]! && p < hi) out.push(p);
  }
  out.push(hi);
  return out;
}

/** True when a log track cannot be drawn over these bounds. */
export function logScaleUnusable(
  bounds: ReadonlyArray<string> | undefined,
  scale: string | null | undefined,
): boolean {
  return scale === "log" && Boolean(bounds && bounds.length >= 2) && Number(bounds![0]) <= 0;
}

/** Position 0..1 of ``v`` on the track (linear or log). */
export function trackPosition(v: number, lo: number, hi: number, scale?: string | null): number {
  if (hi <= lo) return 0;
  if (scale === "log" && lo > 0 && v > 0) {
    return (Math.log10(v) - Math.log10(lo)) / (Math.log10(hi) - Math.log10(lo));
  }
  return (v - lo) / (hi - lo);
}

/** Value at track position ``t`` (inverse of ``trackPosition``). */
export function valueAtPosition(t: number, lo: number, hi: number, scale?: string | null): number {
  if (scale === "log" && lo > 0) {
    return Math.pow(10, Math.log10(lo) + t * (Math.log10(hi) - Math.log10(lo)));
  }
  return lo + t * (hi - lo);
}

/**
 * Interior boundaries of ``bins`` equal-width histogram bins over the
 * track (equal in the TRACK's scale, so a log track gets log bins and the
 * bars sit under the handles). The engine counts ``edges[i-1] <= v <
 * edges[i]``; values below the first edge land in bin 0.
 */
export function histogramEdges(
  lo: number,
  hi: number,
  scale: string | null | undefined,
  bins = 24,
): number[] {
  const out: number[] = [];
  for (let i = 1; i < bins; i++) out.push(valueAtPosition(i / bins, lo, hi, scale));
  return out;
}

/** Bin ``i`` lies inside the applied range when its centre does. */
export function binInRange(
  i: number,
  bins: number,
  lo: number,
  hi: number,
  scale: string | null | undefined,
  applied: RangeBounds | null,
): boolean {
  const centre = valueAtPosition((i + 0.5) / bins, lo, hi, scale);
  return (
    (applied?.min == null || centre >= applied.min) &&
    (applied?.max == null || centre <= applied.max)
  );
}

/** Response of ``GET /{key}/filters/{filter_key}/histogram``. */
export interface RangeHistogram {
  bins: number[];
  nulls: number;
  total: number;
  in_range: number;
}

/** Host-supplied fetcher: the host closes over its active filters. */
export type FetchRangeHistogram = (
  filterKey: string,
  edges: number[],
  signal: AbortSignal,
) => Promise<RangeHistogram>;

/** Index of the domain point nearest to ``v``. */
export function nearestNumericIndex(domain: ReadonlyArray<number>, v: number): number {
  let best = 0;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (let i = 0; i < domain.length; i++) {
    const delta = Math.abs(domain[i]! - v);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  }
  return best;
}

/** Handle indices for a value: an open side rests at its extreme. */
export function handleIndices(
  domain: ReadonlyArray<number>,
  value: RangeBounds | null,
): [number, number] {
  const last = Math.max(domain.length - 1, 0);
  let lo = value?.min != null ? nearestNumericIndex(domain, value.min) : 0;
  let hi = value?.max != null ? nearestNumericIndex(domain, value.max) : last;
  if (lo > hi) [lo, hi] = [hi, lo];
  return [lo, hi];
}

/**
 * Wire value for a slider position. A handle parked on its extreme
 * means "no bound on this side", dragging both handles back to the
 * ends clears the filter instead of pinning the current data extremes
 * (which would also drop rows whose metric is NULL).
 */
export function rangeFromIndices(
  domain: ReadonlyArray<number>,
  lo: number,
  hi: number,
): string {
  const last = domain.length - 1;
  return encodeRange(lo <= 0 ? null : domain[lo]!, hi >= last ? null : domain[hi]!);
}

/**
 * Wire value at the end of a drag. Like ``rangeFromIndices``, but a side
 * whose handle did NOT move keeps the value that was applied before the
 * drag. A typed bound rarely sits on a snap point ($100K on a track that
 * steps every $500K): its handle rests on the nearest point, and without
 * this, dragging the OTHER handle would silently rewrite (or drop) the
 * typed bound.
 */
export function rangeFromDrag(
  domain: ReadonlyArray<number>,
  dragged: readonly [number, number],
  before: readonly [number, number],
  applied: RangeBounds | null,
): string {
  const last = domain.length - 1;
  const [lo, hi] = dragged;
  const min =
    lo === before[0] ? (applied?.min ?? null) : lo <= 0 ? null : domain[lo]!;
  const max =
    hi === before[1] ? (applied?.max ?? null) : hi >= last ? null : domain[hi]!;
  return encodeRange(min, max);
}

/**
 * Handle labels while no drag is in flight: the snap-point labels, with
 * each bound's handle showing the APPLIED value (an off-grid $100K, not
 * the $154.70 snap point the handle rests on). During a drag the plain
 * snap labels are what the handles mean.
 */
export function appliedLabels(
  labels: ReadonlyArray<string>,
  handles: readonly [number, number],
  applied: RangeBounds | null,
  fmt: string | null | undefined,
): string[] {
  const out = [...labels];
  if (applied?.min != null) out[handles[0]] = formatRangeBound(applied.min, fmt);
  if (applied?.max != null && handles[1] !== handles[0]) {
    out[handles[1]] = formatRangeBound(applied.max, fmt);
  }
  return out;
}

/** Factor between the column value and what the user types. A
 *  ``percent`` column is a 0..1 fraction; the user types 30 for 30 %. */
export function inputScale(fmt: string | null | undefined): number {
  return fmt === "percent" ? 100 : 1;
}

/** Display string for one bound (slider labels and the active chip). */
export function formatRangeBound(v: number, fmt: string | null | undefined): string {
  if (fmt === "percent") return formatValue(v, "percent");
  if (fmt === "currency") return formatValue(v, "currency", { compact: true });
  return formatValue(v, "number");
}
